/**
 * TCAPLoader
 * A flexible loading component with orbiting shapes animation.
 * @param {string} variant - 'light' or 'dark' theme (default: 'light')
 * @param {string} text - Loading text to display (default: 'Loading')
 * @param {number} scale - Multiplier for component size (default: 1)
 * @param {string} className - Additional CSS classes
 * @param {boolean} showText - false renders the shapes alone, without the pill and text (default: true)
 */
const TCAPLoader = (props) => {
    const {
        variant = 'light',
        text = 'Loading',
        scale = 1,
        className = '',
        showText = true,
    } = props;
    const isDark = variant === 'dark';
    const speed = 2.5;

    // Base dimensions (at scale 1)
    const baseHeight = 36;
    const baseSpinnerSize = 32;
    const baseFontSize = 12;

    // Scaled dimensions
    const height = baseHeight * scale;
    const spinnerSize = baseSpinnerSize * scale;
    const fontSize = baseFontSize * scale;

    const shapes = [
        {
            id: 'circle',
            color: '#7040a3',
            path: 'M 1350,1400 m -450,0 a 450,450 0 1,0 900,0 a 450,450 0 1,0 -900,0',
        },
        {
            id: 'triangle',
            color: '#7fca36',
            path: 'M 1350,1900 L 1783,1150 L 917,1150 Z',
        },
        {
            id: 'square',
            color: '#51a2de',
            path: 'M 1000,1000 h 600 a 100,100 0 0 1 100,100 v 600 a 100,100 0 0 1 -100,100 h -600 a 100,100 0 0 1 -100,-100 v -600 a 100,100 0 0 1 100,-100 z',
        },
    ];

    const transformOrigin = '1350px 1450px';

    const spinner = (
        <svg
            viewBox="0 0 2500 2500"
            className="overflow-visible"
            style={{
                transform: 'scaleY(-1)',
                width: `${spinnerSize}px`,
                height: `${spinnerSize}px`,
            }}>
            {shapes.map((shape, index) => (
                <g
                    key={shape.id}
                    style={{
                        transformOrigin,
                        animation: `circular-orbit-tcap ${speed}s cubic-bezier(0.45, 0, 0.55, 1) infinite`,
                        animationDelay: `-${(speed / 3) * index}s`,
                    }}>
                    <path d={shape.path} fill={shape.color} fillOpacity={1} />
                </g>
            ))}
        </svg>
    );

    if (!showText) {
        return (
            <div
                className={`pointer-events-none flex items-center justify-center ${className}`}>
                {spinner}
            </div>
        );
    }

    return (
        <div
            className={`pointer-events-none flex items-center justify-center ${className}`}>
            <div
                className={`flex w-fit items-center justify-start rounded-full border-[1px] shadow-2xl transition-all duration-300 ${
                    isDark
                        ? 'border-gray-800 bg-black text-white'
                        : 'border-gray-200 bg-white text-black'
                }`}
                style={{
                    height: `${height}px`,
                    paddingLeft: `${4 * scale}px`,
                    paddingRight: `${12 * scale}px`,
                    gap: `${4 * scale}px`,
                }}>
                {spinner}
                <span
                    className="mb-0.5 font-['Inter'] font-medium leading-none tracking-tight"
                    style={{ fontSize: `${fontSize}px` }}>
                    {text}
                    <span className="tcap-dots"></span>
                </span>
            </div>
        </div>
    );
};

// Backward-compatible exports
const TCAPLoaderLight = (props) => <TCAPLoader {...props} variant="light" />;
const TCAPLoaderDark = (props) => <TCAPLoader {...props} variant="dark" />;

export { TCAPLoader, TCAPLoaderLight, TCAPLoaderDark };
export default TCAPLoader;
